/**
 * useModules Hook + Context
 * 
 * PR-5X-C: Phase 5X Modul-System
 * 
 * Zentrale Modul-Verwaltung für das Frontend.
 * Lädt Module einmal und cached sie.
 * 
 * Usage:
 *   const { isEnabled, maturity, getModule } = useModules();
 *   if (isEnabled('tech')) { ... }
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from '../App';

export type ModuleMaturity = 'STABLE' | 'BETA' | 'EXPERIMENTAL' | 'DEPRECATED';

export interface ModuleEffective {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  maturity: ModuleMaturity;
  tooltip: string;
  hasOverride: boolean;
  overrideEnabled: boolean | null;
  overrideMaturity: ModuleMaturity | null;
  defaultEnabled: boolean;
  defaultMaturity: ModuleMaturity;
}

interface ModulesContextType {
  modules: ModuleEffective[];
  loading: boolean;
  error: string | null;
  isEnabled: (key: string) => boolean;
  isBeta: (key: string) => boolean;
  maturity: (key: string) => ModuleMaturity | null;
  getModule: (key: string) => ModuleEffective | undefined;
  getTooltip: (key: string) => string | undefined;
  refresh: () => Promise<void>;
}

const ModulesContext = createContext<ModulesContextType>({
  modules: [],
  loading: true,
  error: null,
  isEnabled: () => false,
  isBeta: () => false,
  maturity: () => null,
  getModule: () => undefined,
  getTooltip: () => undefined,
  refresh: async () => {},
});

interface ModulesProviderProps {
  children: ReactNode;
}

export function ModulesProvider({ children }: ModulesProviderProps) {
  const { user } = useAuth();
  const [modules, setModules] = useState<ModuleEffective[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModules = useCallback(async () => {
    // KRITISCH: /api/system/modules nur bei User-Session laden!
    // Im Station-only/Chip-Flow (user === null) darf KEIN Request passieren.
    if (!user) {
      console.info('[useModules] Skipping - no user session (station-only mode)');
      setModules([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/system/modules', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch modules');
      }
      
      const data = await response.json();
      setModules(data);
    } catch (err) {
      console.error('[useModules] Error fetching modules:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Fallback: alle Module als enabled betrachten wenn API nicht erreichbar
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const isEnabled = useCallback((key: string): boolean => {
    const module = modules.find(m => m.key === key);
    // Fallback: wenn Module nicht geladen, als enabled betrachten (graceful degradation)
    if (!module && loading) return true;
    return module?.enabled ?? true;
  }, [modules, loading]);

  const isBeta = useCallback((key: string): boolean => {
    const module = modules.find(m => m.key === key);
    if (!module) return false;
    return module.maturity === 'BETA' || module.maturity === 'EXPERIMENTAL';
  }, [modules]);

  const getMaturity = useCallback((key: string): ModuleMaturity | null => {
    const module = modules.find(m => m.key === key);
    return module?.maturity ?? null;
  }, [modules]);

  const getModule = useCallback((key: string): ModuleEffective | undefined => {
    return modules.find(m => m.key === key);
  }, [modules]);

  const getTooltip = useCallback((key: string): string | undefined => {
    const module = modules.find(m => m.key === key);
    return module?.tooltip;
  }, [modules]);

  const value: ModulesContextType = {
    modules,
    loading,
    error,
    isEnabled,
    isBeta,
    maturity: getMaturity,
    getModule,
    getTooltip,
    refresh: fetchModules,
  };

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf Module-System
 */
export function useModules(): ModulesContextType {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return context;
}

/**
 * Maturity Badge Styles
 */
export const MATURITY_STYLES: Record<ModuleMaturity, string> = {
  STABLE: '',
  BETA: 'bg-orange-50 text-orange-600 border-orange-200',
  EXPERIMENTAL: 'bg-purple-50 text-purple-600 border-purple-200',
  DEPRECATED: 'bg-red-50 text-red-600 border-red-200',
};

/**
 * Maturity Badge Labels
 */
export const MATURITY_LABELS: Record<ModuleMaturity, string> = {
  STABLE: '',
  BETA: 'BETA',
  EXPERIMENTAL: 'EXPERIMENTAL',
  DEPRECATED: 'DEPRECATED',
};
